import { useState, useEffect } from "react";
import { API, useAuth } from "@/App";
import { toast } from "sonner";
import {
  Users,
  Building2,
  Wrench,
  Package,
  Search,
  RefreshCw,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Mail,
  Send,
  Trash2,
  Eye,
  Filter,
  Download,
  Calendar,
  User,
  Building,
  ExternalLink,
  MoreHorizontal,
  ChevronDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export default function RegistrationLeads() {
  const { token } = useAuth();
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [portalFilter, setPortalFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedReg, setSelectedReg] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [stats, setStats] = useState({ total: 0, pending: 0, completed: 0, expired: 0 });

  const fetchRegistrations = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (portalFilter !== "all") params.append("portal_type", portalFilter);
      if (statusFilter !== "all") params.append("status", statusFilter);
      
      const res = await fetch(`${API}/admin/registrations?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setRegistrations(data.registrations || []);
        setStats(data.stats || { total: 0, pending: 0, completed: 0, expired: 0 });
      } else {
        toast.error("Failed to load registrations");
      }
    } catch (err) {
      console.error("Error fetching registrations:", err);
      toast.error("Network error loading registrations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRegistrations();
  }, [portalFilter, statusFilter]);

  const handleResendEmail = async (regId) => {
    setActionLoading(regId);
    try {
      const res = await fetch(`${API}/admin/registrations/${regId}/resend`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Setup email resent successfully");
        fetchRegistrations();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to resend email");
      }
    } catch (err) {
      toast.error("Network error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleApprove = async (regId) => {
    setActionLoading(regId);
    try {
      const res = await fetch(`${API}/admin/registrations/${regId}/approve`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Registration approved");
        fetchRegistrations();
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to approve");
      }
    } catch (err) {
      toast.error("Network error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (regId) => {
    if (!confirm("Are you sure you want to delete this registration request?")) return;
    
    setActionLoading(regId);
    try {
      const res = await fetch(`${API}/admin/registrations/${regId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success("Registration deleted");
        fetchRegistrations();
        setShowDetailModal(false);
      } else {
        const data = await res.json();
        toast.error(data.detail || "Failed to delete");
      }
    } catch (err) {
      toast.error("Network error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleExportCSV = () => {
    const headers = ["Email", "Name", "Company", "Portal Type", "Status", "Created At"];
    const rows = filteredRegistrations.map(r => [
      r.email,
      r.name || "",
      r.company_name || "",
      r.portal_type,
      r.status,
      new Date(r.created_at).toLocaleString()
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `registration-leads-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  const filteredRegistrations = registrations.filter(reg => {
    const matchesSearch = 
      reg.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reg.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reg.company_name?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const getPortalIcon = (type) => {
    switch (type) {
      case "hospital": return Building2;
      case "contractor": return Wrench;
      case "supplier": return Package;
      default: return Users;
    }
  };

  const getPortalColor = (type) => {
    switch (type) {
      case "hospital": return "bg-cyan-100 text-cyan-700 border-cyan-200";
      case "contractor": return "bg-violet-100 text-violet-700 border-violet-200";
      case "supplier": return "bg-amber-100 text-amber-700 border-amber-200";
      default: return "bg-slate-100 text-slate-700 border-slate-200";
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case "completed":
        return <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      case "expired":
        return <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200"><XCircle className="w-3 h-3 mr-1" />Expired</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { 
      month: "short", 
      day: "numeric", 
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  return (
    <div className="space-y-6" data-testid="registration-leads">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            Registration Leads
          </h2>
          <p className="text-slate-500 mt-1">Manage self-service registration requests</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleExportCSV}
            className="gap-2"
            data-testid="export-csv-btn"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </Button>
          <Button
            variant="outline"
            onClick={fetchRegistrations}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Total Leads</p>
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
            </div>
            <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-indigo-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Pending</p>
              <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
            </div>
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Completed</p>
              <p className="text-2xl font-bold text-emerald-600">{stats.completed}</p>
            </div>
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-500">Expired</p>
              <p className="text-2xl font-bold text-red-600">{stats.expired}</p>
            </div>
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by email, name, or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <Select value={portalFilter} onValueChange={setPortalFilter}>
            <SelectTrigger className="w-full sm:w-[160px]" data-testid="portal-filter">
              <SelectValue placeholder="Portal Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Portals</SelectItem>
              <SelectItem value="hospital">Hospital</SelectItem>
              <SelectItem value="contractor">Contractor</SelectItem>
              <SelectItem value="supplier">Supplier</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-[140px]" data-testid="status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="expired">Expired</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
          </div>
        ) : filteredRegistrations.length === 0 ? (
          <div className="text-center py-20">
            <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-1">No Registration Leads</h3>
            <p className="text-slate-500">
              {searchTerm || portalFilter !== "all" || statusFilter !== "all"
                ? "No leads match your filters"
                : "Registration requests will appear here when users sign up"}
            </p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">Contact</TableHead>
                <TableHead className="font-semibold">Portal</TableHead>
                <TableHead className="font-semibold">Status</TableHead>
                <TableHead className="font-semibold">Registered</TableHead>
                <TableHead className="font-semibold text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRegistrations.map((reg) => {
                const PortalIcon = getPortalIcon(reg.portal_type);
                return (
                  <TableRow 
                    key={reg.id} 
                    className="hover:bg-slate-50 cursor-pointer"
                    onClick={() => { setSelectedReg(reg); setShowDetailModal(true); }}
                    data-testid={`reg-row-${reg.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-slate-500" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{reg.name || "Not provided"}</p>
                          <p className="text-sm text-slate-500">{reg.email}</p>
                          {reg.company_name && (
                            <p className="text-xs text-slate-400 flex items-center gap-1">
                              <Building className="w-3 h-3" />
                              {reg.company_name}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getPortalColor(reg.portal_type)}`}>
                        <PortalIcon className="w-3.5 h-3.5" />
                        <span className="capitalize">{reg.portal_type}</span>
                      </div>
                    </TableCell>
                    <TableCell>{getStatusBadge(reg.status)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-slate-600">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(reg.created_at)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setSelectedReg(reg); setShowDetailModal(true); }}>
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          {reg.status === "pending" && (
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleResendEmail(reg.id); }}>
                              <Send className="w-4 h-4 mr-2" />
                              Resend Email
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem 
                            onClick={(e) => { e.stopPropagation(); handleDelete(reg.id); }}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <User className="w-5 h-5 text-indigo-600" />
              Registration Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedReg && (
            <div className="space-y-4">
              {/* Contact Info */}
              <div className="bg-slate-50 rounded-xl p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center border border-slate-200">
                    <User className="w-6 h-6 text-slate-500" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">{selectedReg.name || "Name not provided"}</p>
                    <p className="text-sm text-slate-500">{selectedReg.email}</p>
                  </div>
                </div>
                
                {selectedReg.company_name && (
                  <div className="flex items-center gap-2 text-sm">
                    <Building className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">{selectedReg.company_name}</span>
                  </div>
                )}
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500 mb-1">Portal Type</p>
                  <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getPortalColor(selectedReg.portal_type)}`}>
                    {(() => { const Icon = getPortalIcon(selectedReg.portal_type); return <Icon className="w-3.5 h-3.5" />; })()}
                    <span className="capitalize">{selectedReg.portal_type}</span>
                  </div>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Status</p>
                  {getStatusBadge(selectedReg.status)}
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Registered On</p>
                  <p className="text-sm font-medium text-slate-700">{formatDate(selectedReg.created_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 mb-1">Token Expires</p>
                  <p className="text-sm font-medium text-slate-700">{formatDate(selectedReg.setup_token_expiry)}</p>
                </div>
              </div>

              {/* Source */}
              <div className="text-xs text-slate-400 pt-2 border-t border-slate-100">
                Source: {selectedReg.source || "self_registration"} • ID: {selectedReg.id}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            {selectedReg?.status === "pending" && (
              <Button
                variant="outline"
                onClick={() => handleResendEmail(selectedReg.id)}
                disabled={actionLoading === selectedReg?.id}
                className="gap-2"
              >
                {actionLoading === selectedReg?.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                Resend Email
              </Button>
            )}
            <Button
              variant="destructive"
              onClick={() => handleDelete(selectedReg?.id)}
              disabled={actionLoading === selectedReg?.id}
              className="gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
